import { useEffect, useRef } from 'react';
import { Control, useController, UseFormReturn } from 'react-hook-form';
import { v4 } from 'uuid';
import { AttributeOption, EventPanelFormType } from '@mark43/cad-api';
import { useIntegratedActionBroadcaster } from '../../action-broadcaster/use-integrated-action-broadcaster';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';
import { useIntegratedForm } from '../../forms/use-integrated-form';
import { useWorkspacePanel } from '../../panels/hooks/use-workspace-panel';
import { useFormHasHadDataSetAtLeastOnceContext } from '../event-state/form-has-had-data-set-at-least-once';
import { useEventMetadataStore } from './event-metadata';

// Module-level, so a bootstrap without options does not hand the effect a new array every render.
const NO_PRIORITY_OPTIONS: AttributeOption[] = [];

/**
 * Prefills a new event from the sensor event it was created from (Create event on a sensor
 * event), using what that put on the panel's state:
 *
 * - `sensorAlertKeys`: the alert is linked when the event is saved.
 * - `initialPriority`: the priority mapped to the alert's type, if the department offers it and
 *   none is picked yet.
 * - `initialLocationQuery`: typed into this panel's Location field with the picker open, so the
 *   dispatcher picks the location and the panel's own classification applies.
 *
 * Runs once per mounted panel, after the form's first load (which would otherwise undo it), and
 * only while the event is unsaved. Values are set as a dispatcher's would be — dirty — so the
 * resets a new event goes through before it is saved keep them, and the save's own reset clears
 * them, which is what stops a later save sending the keys again.
 *
 * Returns whether the alert link is still pending, i.e. whether the panel must render
 * `SensorAlertKeysField` to keep the keys registered until the save.
 */
export function useSensorEventPrefill(form: UseFormReturn<EventPanelFormType>): boolean {
    const workspacePanel = useWorkspacePanel<'EVENT'>();
    const sensorAlertKeys = workspacePanel.getPanelState('sensorAlertKeys');
    const initialPriority = workspacePanel.getPanelState('initialPriority');
    const locationQuery = workspacePanel.getPanelState('initialLocationQuery');
    const clientUuid = workspacePanel.uuid;
    const eventExists = !!useEventMetadataStore()?.state.metadata?.eventId;
    const isFormInitialized = useFormHasHadDataSetAtLeastOnceContext();
    const { inputHandleManager } = useIntegratedForm<EventPanelFormType>();
    const { trigger } = useIntegratedActionBroadcaster();
    const { eventPanelOptions } = useBootstrapState();
    const priorityOptions = eventPanelOptions?.priorityOptions ?? NO_PRIORITY_OPTIONS;
    const hasRunRef = useRef(false);

    useEffect(
        function prefillFromSensorEvent() {
            const hasPrefill = !!sensorAlertKeys?.length || !!initialPriority || !!locationQuery;
            if (hasRunRef.current || !hasPrefill || eventExists || !isFormInitialized) {
                return;
            }
            hasRunRef.current = true;

            if (sensorAlertKeys?.length) {
                // Registered by `SensorAlertKeysField`, which the panel renders while this is
                // pending; without that, the resets before the first save would drop the keys.
                form.setValue('sensorAlertKeys', sensorAlertKeys, { shouldDirty: true });
            }

            if (initialPriority && !form.getFieldState('priority').isDirty) {
                // The select's own option, so the value is one the select recognises.
                const option = priorityOptions.find(
                    (candidate) => candidate.value === initialPriority.value
                );
                if (option) {
                    form.setValue('priority', option, { shouldDirty: true });
                }
            }

            if (locationQuery && !form.getValues('location')) {
                inputHandleManager.getHandle('location')?.setEditableMode?.('edit');
                trigger([
                    {
                        id: v4(),
                        targetWindowType: 'EVENT_V2',
                        type: 'SET_LOCATION_QUERY',
                        payload: {
                            query: locationQuery,
                            fieldIdentifier: `${clientUuid}:EVENT_LOCATION`,
                        },
                    },
                ]);
            }
        },
        [
            sensorAlertKeys,
            initialPriority,
            locationQuery,
            clientUuid,
            eventExists,
            isFormInitialized,
            form,
            inputHandleManager,
            trigger,
            priorityOptions,
        ]
    );

    return !!sensorAlertKeys?.length && !eventExists;
}

/**
 * Keeps `sensorAlertKeys` registered with the form. No input renders the field, and every
 * react-hook-form reset forgets all registrations, while `keepDirtyValues` keeps only registered
 * fields. Registering once would therefore survive one reset and lose the keys to the next, and a
 * new event resets on every socket reconnect. `useController` registers on each render and
 * re-renders after each reset, which the panel body (an observer, so memoised) does not.
 */
export function SensorAlertKeysField({ control }: { control: Control<EventPanelFormType> }): null {
    useController({ control, name: 'sensorAlertKeys' });
    return null;
}
